import { Dispatch, SetStateAction, useEffect } from "react";
import { useCellCitationDetail } from "../../../../hooks/audit/useCellCitationDetail";
import {
  deserialiseCitationFromString,
  extractTimestampFromCitationString,
} from "../../../../models/audit/citation";
import type { BoundingBox } from "../../../extraction/viewExtraction/FileViewer";

type BoundingBoxEntry = {
  id?: string;
  boundingBox: BoundingBox | undefined;
  elementId: string;
  pageWidth: number;
  pageHeight: number;
  pageNumber: number;
  shortSummary?: string;
  documentId?: string;
  timestampSeconds?: number;
};

interface CitationDetailHydratorProps {
  procedureSessionId: string;
  sourceRowId: string;
  supportColumnId: string;
  setCurrentBoundingBoxes: Dispatch<
    SetStateAction<BoundingBoxEntry[] | undefined>
  >;
  setCurrentPage: Dispatch<SetStateAction<number>>;
  setSelectedBoundingIndex: Dispatch<SetStateAction<number>>;
}

/**
 * Workpaper-lazy-load: side-panel companion that lazy-fetches the citation
 * list for the currently-selected support cell and pushes the derived
 * bounding boxes into the parent's side-panel state.
 *
 * Mounted by `ProcedureSessionWorkPaper` only when the citation viewer is
 * actually visible (`showRightPanel && currentSelection.sourceRowId &&
 * currentSelection.supportColumnId`). Replaces the inline `citations`
 * derivation that used to live in `handleCheckSelectionV2` — the slim
 * `workpaperData` payload no longer carries `citations[]`, so the bounding
 * boxes must come from the per-cell lazy hook instead.
 */
export const CitationDetailHydrator = ({
  procedureSessionId,
  sourceRowId,
  supportColumnId,
  setCurrentBoundingBoxes,
  setCurrentPage,
  setSelectedBoundingIndex,
}: CitationDetailHydratorProps) => {
  const { data } = useCellCitationDetail(
    procedureSessionId,
    sourceRowId,
    supportColumnId,
  );

  useEffect(() => {
    if (!data) {
      // Selection moved to a cell whose citations are still loading — clear
      // the previous cell's boxes so the panel doesn't show stale highlights.
      // This reset must live HERE, not in a parent effect keyed on the
      // selection: on a warm cache `useCellCitationDetail` returns data
      // synchronously, so a parent reset fires in the same commit as the
      // populate below — and parent effects run after children, wiping the
      // just-hydrated boxes (empty summary/pager on every revisit).
      setCurrentBoundingBoxes(undefined);
      setSelectedBoundingIndex(0);
      setCurrentPage(1);
      return;
    }
    const citations = data.citations ?? [];
    if (citations.length === 0) {
      setCurrentBoundingBoxes(undefined);
      setSelectedBoundingIndex(0);
      setCurrentPage(1);
      return;
    }
    const newBoundingBoxes: BoundingBoxEntry[] = citations.map((citation) => ({
      id: citation.id,
      boundingBox: citation.boundingBox ?? undefined,
      pageNumber: citation.pageNumber ?? 1,
      elementId: deserialiseCitationFromString(
        citation.encodedCitationString ?? "",
      )[1],
      pageWidth: citation.pageWidth ?? 1,
      pageHeight: citation.pageHeight ?? 1,
      shortSummary: citation.shortSummary ?? undefined,
      documentId: citation.documentId,
      timestampSeconds:
        citation.timestampSeconds ??
        extractTimestampFromCitationString(
          citation.encodedCitationString ?? "",
        ),
    }));
    setCurrentBoundingBoxes(newBoundingBoxes);
    setSelectedBoundingIndex(0);
    setCurrentPage(newBoundingBoxes[0].pageNumber);
  }, [data, setCurrentBoundingBoxes, setCurrentPage, setSelectedBoundingIndex]);

  return null;
};
